import { zodResolver } from "@hookform/resolvers/zod";
import { RotateCcw } from "lucide-react";
import { type Control, useForm } from "react-hook-form";
import { z } from "zod";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type ThemeSettings,
  useThemeSettings,
  useUpdateThemeSettings,
} from "@/hooks/use-theme-settings";

// Form fields hold "" for "use the built-in default"; the API stores null.
const hexField = z.union([
  z.literal(""),
  z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a 6-digit hex color, e.g. #001847"),
]);
const fontField = z.union([
  z.literal(""),
  z
    .string()
    .trim()
    .max(64)
    .regex(/^[A-Za-z0-9 ]+$/, "Letters, digits, and spaces only"),
]);

const themeFormSchema = z.object({
  primaryColor: hexField,
  accentColor: hexField,
  sidebarColor: hexField,
  fontBody: fontField,
  fontDisplay: fontField,
});

type ThemeFormValues = z.infer<typeof themeFormSchema>;

// Curated Google Fonts families offered in the pickers — all load from
// fonts.googleapis.com at runtime (see ThemeOverrides). Alphabetical.
const FONT_OPTIONS = [
  "DM Sans",
  "IBM Plex Sans",
  "Inter",
  "Lato",
  "Merriweather",
  "Montserrat",
  "Nunito",
  "Open Sans",
  "Playfair Display",
  "Poppins",
  "Raleway",
  "Roboto",
  "Rubik",
  "Source Sans 3",
  "Work Sans",
];

// Radix Select disallows "" item values — sentinel for "use the default".
const FONT_DEFAULT = "__default__";

export function SettingsThemePage() {
  const { data: theme, isLoading, isError, error, refetch } = useThemeSettings();

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load theme settings"}
        onRetry={() => refetch()}
      />
    );
  }

  if (isLoading || !theme) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Key by the stored values so a background refetch after save re-seeds the
  // form defaults (and isDirty) instead of leaving them stale.
  return <ThemeForm key={JSON.stringify(theme)} theme={theme} />;
}

const COLOR_FIELDS = [
  {
    name: "primaryColor",
    label: "Primary color",
    placeholder: "#001847",
    description: "Buttons, links, and active states. Text on it flips light/dark automatically.",
  },
  {
    name: "accentColor",
    label: "Accent color",
    placeholder: "#e8f2fb",
    description: "Tinted hover and selection surfaces.",
  },
  {
    name: "sidebarColor",
    label: "Sidebar color",
    placeholder: "#001847",
    description: "The navigation shell. Hover and text shades are derived from it.",
  },
] as const;

function FontSelectField({
  control,
  name,
  label,
  defaultLabel,
  description,
}: {
  control: Control<ThemeFormValues>;
  name: "fontBody" | "fontDisplay";
  label: string;
  defaultLabel: string;
  description: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Select
            value={field.value === "" ? FONT_DEFAULT : field.value}
            onValueChange={(v) => field.onChange(v === FONT_DEFAULT ? "" : v)}
          >
            <FormControl>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value={FONT_DEFAULT}>
                <span className="text-muted-foreground">{defaultLabel}</span>
              </SelectItem>
              {FONT_OPTIONS.map((font) => (
                <SelectItem key={font} value={font}>
                  {font}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormDescription>{description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function ThemeForm({ theme }: { theme: ThemeSettings }) {
  const update = useUpdateThemeSettings();

  const form = useForm<ThemeFormValues>({
    resolver: zodResolver(themeFormSchema),
    defaultValues: {
      primaryColor: theme.primaryColor ?? "",
      accentColor: theme.accentColor ?? "",
      sidebarColor: theme.sidebarColor ?? "",
      fontBody: theme.fontBody ?? "",
      fontDisplay: theme.fontDisplay ?? "",
    },
  });

  function onSubmit(values: ThemeFormValues) {
    update.mutate({
      primaryColor: values.primaryColor || null,
      accentColor: values.accentColor || null,
      sidebarColor: values.sidebarColor || null,
      fontBody: values.fontBody.trim() || null,
      fontDisplay: values.fontDisplay.trim() || null,
    });
  }

  function resetToDefaults() {
    form.setValue("primaryColor", "");
    form.setValue("accentColor", "");
    form.setValue("sidebarColor", "");
    form.setValue("fontBody", "");
    form.setValue("fontDisplay", "");
    update.mutate({
      primaryColor: null,
      accentColor: null,
      sidebarColor: null,
      fontBody: null,
      fontDisplay: null,
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Colors</CardTitle>
            <CardDescription>
              Override the workspace color scheme with hex codes. Leave a field empty to keep the
              built-in default. Overrides apply to both light and dark mode for everyone.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-6 sm:grid-cols-3">
            {COLOR_FIELDS.map(({ name, label, placeholder, description }) => (
              <FormField
                key={name}
                control={form.control}
                name={name}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{label}</FormLabel>
                    <div className="flex items-center gap-2">
                      <FormControl>
                        <Input placeholder={placeholder} {...field} />
                      </FormControl>
                      {/* Native color-wheel picker, two-way synced with the hex
                          field. While the hex is empty/invalid it previews the
                          slot's default; picking a color writes the hex back. */}
                      <input
                        type="color"
                        aria-label={`Pick ${label.toLowerCase()}`}
                        value={/^#[0-9a-fA-F]{6}$/.test(field.value) ? field.value : placeholder}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="h-9 w-10 shrink-0 cursor-pointer rounded-md border bg-background p-1"
                      />
                    </div>
                    <FormDescription>{description}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Fonts</CardTitle>
            <CardDescription>
              Google Fonts, loaded automatically. Pick "default" to keep the built-ins (Raleway
              body, Montserrat headings).
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-6 sm:grid-cols-2">
            <FontSelectField
              control={form.control}
              name="fontBody"
              label="Body font"
              defaultLabel="Raleway (default)"
              description="Paragraphs, tables, and controls."
            />
            <FontSelectField
              control={form.control}
              name="fontDisplay"
              label="Display font"
              defaultLabel="Montserrat (default)"
              description="Page titles and headings."
            />
          </CardContent>
        </Card>

        <div className="flex items-center justify-between">
          {/* Guarded: this clears the workspace's colours and fonts for
              EVERYONE, live, with no undo — and every other destructive action
              in Settings asks first. */}
          <ConfirmDialog
            title="Reset the workspace theme?"
            description="This clears the colours and fonts for everyone in the workspace, straight away. There's no undo — the previous values would have to be typed back in."
            confirmLabel="Reset theme"
            destructive
            onConfirm={resetToDefaults}
            trigger={
              <Button type="button" variant="outline" disabled={update.isPending}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset to defaults
              </Button>
            }
          />
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? "Saving…" : "Save theme"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
